import { useState } from "react";
import { toast } from "sonner";
import { X, Send, CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const field = "w-full rounded-xl border border-[#D7E3F2] bg-white px-4 py-3 text-[#0D1B2A] placeholder:text-[#94A3B8] outline-none transition-colors focus:border-[#377DD3] focus:ring-4 focus:ring-[#377DD3]/10";
const label = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#0D1B2A]";
const empty = { full_name: "", email: "", company: "", phone: "", country: "", message: "" };

export function WholesaleQuoteButton({ region, countries, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid} className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#377DD3] to-[#245FAF] px-6 py-3 text-sm font-semibold text-white shadow-[0_14px_34px_-12px_rgba(55,125,211,0.9)] ring-1 ring-inset ring-[#F5C64D]/50 transition-all hover:scale-[1.02]">
        Request a bulk price list <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
      </button>
      {open && <WholesaleQuoteDialog region={region} countries={countries} onClose={() => setOpen(false)} />}
    </>
  );
}

function WholesaleQuoteDialog({ region, countries, onClose }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.company || !form.country) {
      toast.error("Please fill in your name, email, company and country.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", { ...form, kind: "wholesale_quote", region, role: "Wholesaler / Distributor", message: form.message || `Bulk price list request for ${form.country}.` });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not send your request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-[#0D1B2A]/50 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose} data-testid="wholesale-quote-dialog">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#377DD3]">{region} wholesale</span>
            <h3 className="font-heading mt-1 text-2xl font-bold text-[#0D1B2A]">Request a bulk price list</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="wholesale-quote-close" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#64748B] hover:bg-[#EAF2FB]"><X size={18} /></button>
        </div>

        {done ? (
          <div className="flex flex-col items-center py-10 text-center" data-testid="wholesale-quote-success">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#10B981]/10 text-[#10B981]"><CheckCircle2 size={34} /></div>
            <h4 className="font-heading mt-5 text-xl font-bold text-[#0D1B2A]">Request received</h4>
            <p className="mt-2 max-w-sm text-sm text-[#64748B]">Our trade desk will send the {form.country} price list to {form.email} within one business day.</p>
            <button type="button" onClick={onClose} className="mt-6 text-sm font-semibold text-[#245FAF] hover:underline">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="wholesale-quote-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={label}>Full name</label><input className={field} value={form.full_name} onChange={up("full_name")} placeholder="Jane Doe" data-testid="wq-name" /></div>
              <div><label className={label}>Work email</label><input type="email" className={field} value={form.email} onChange={up("email")} placeholder="you@company.com" data-testid="wq-email" /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={label}>Company</label><input className={field} value={form.company} onChange={up("company")} placeholder="Wholesaler / distributor" data-testid="wq-company" /></div>
              <div><label className={label}>Phone / WhatsApp <span className="normal-case text-[#94A3B8]">(optional)</span></label><input className={field} value={form.phone} onChange={up("phone")} placeholder="+971 …" data-testid="wq-phone" /></div>
            </div>
            <div>
              <label className={label}>Country</label>
              <select className={field} value={form.country} onChange={up("country")} data-testid="wq-country">
                <option value="">Select your country</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="Other">Other</option>
              </select>
            </div>
            <div><label className={label}>Products of interest <span className="normal-case text-[#94A3B8]">(optional)</span></label><textarea rows={3} className={field} value={form.message} onChange={up("message")} placeholder="Molecules, brands or therapeutic areas you need…" data-testid="wq-message" /></div>
            <button type="submit" disabled={busy} data-testid="wq-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#377DD3] to-[#245FAF] px-7 py-3.5 text-base font-semibold text-white shadow-[0_14px_34px_-12px_rgba(55,125,211,0.9)] ring-1 ring-inset ring-[#F5C64D]/50 transition-all hover:scale-[1.01] disabled:opacity-60">
              {busy ? "Sending…" : <>Send request <Send size={17} /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
